// Single source of truth for the admin tabs' default-view queries: the
// user-scoped cache resource each hook seeds from and the GET that fills
// it. Shared by useCachedQuery callers and the nav prefetcher so the two
// can never drift on resource name or URL.

export type AdminQueryDef = {
  readonly resource: string;
  readonly url: string;
  // Rows persisted to localStorage for large newest-first lists; the
  // revalidate restores the full set. Applied only to array payloads.
  readonly persistCap?: number;
  // Unwraps an envelope response so the cache stores what the hook reads.
  readonly unwrap?: (body: unknown) => unknown;
};

export const ADMIN_QUERIES = {
  users: { resource: "admin:users", url: "/api/admin/users" },
  invites: { resource: "admin:invites", url: "/api/admin/invites" },
  claws: { resource: "admin:claws", url: "/api/admin/claws" },
  events: { resource: "admin:events", url: "/api/admin/events?limit=500", persistCap: 100 },
  toolCalls: { resource: "admin:tool-calls", url: "/api/admin/tool-calls?limit=50", persistCap: 50 },
  resources: { resource: "admin:resources", url: "/api/admin/resources" },
  providers: {
    resource: "admin:providers",
    url: "/api/admin/integration-catalog/providers?includeInactive=false",
    unwrap: (body: unknown): unknown => (body as { providers: unknown }).providers,
  },
  featureFlags: { resource: "admin:feature-flags", url: "/api/admin/feature-flags" },
  secureNotes: { resource: "admin:secure-notes", url: "/api/admin/secure-notes" },
  audit: { resource: "admin:audit", url: "/api/admin/audit/events" },
  status: { resource: "admin:status", url: "/api/admin/claws/health/history?hours=24" },
  rateLimits: { resource: "admin:rate-limits", url: "/api/admin/rate-limits" },
  billing: { resource: "admin:billing", url: "/api/admin/billing/subscriptions?limit=100" },
  tiers: { resource: "admin:tiers", url: "/api/admin/billing/tiers" },
  plugins: { resource: "admin:plugins", url: "/api/admin/plugins" },
  announcements: { resource: "admin:announcements", url: "/api/admin/announcements" },
  supportMessages: { resource: "admin:support-messages", url: "/api/admin/support-messages" },
} as const satisfies Record<string, AdminQueryDef>;

export const ADMIN_TOOL_USAGE_RESOURCE = "admin:tool-usage";

// Per-instance dashboard cache resources, namespaced by tenant so they never
// collide with the fleet `admin:status` (or each other). Only the default
// (24h) range is cached; longer ranges are network-only.
export const instanceHealthResource = (tenantId: string): string => `admin:instance:${tenantId}:health`;
export const instanceUsageResource = (tenantId: string): string => `admin:instance:${tenantId}:usage`;
export const instanceToolCallsResource = (tenantId: string): string => `admin:instance:${tenantId}:tool-calls`;
export const instanceToolLogsResource = (tenantId: string): string => `admin:instance:${tenantId}:tool-logs`;
export const instanceMetricsResource = (tenantId: string): string => `admin:instance:${tenantId}:metrics`;
export const instanceSnapshotsResource = (tenantId: string): string => `admin:instance:${tenantId}:snapshots`;
export const instanceBrowserSettingsResource = (tenantId: string): string => `admin:instance:${tenantId}:browser-settings`;
export const instancePluginUsageResource = (tenantId: string): string => `admin:instance:${tenantId}:plugin-usage`;
export const instanceModelUsageResource = (tenantId: string): string => `admin:instance:${tenantId}:model-usage`;

// Default-range (24h) defs for the row-hover prefetch on the Instances table.
export const instanceHealthDef = (tenantId: string): AdminQueryDef => ({
  resource: instanceHealthResource(tenantId),
  url: `/api/admin/claws/${encodeURIComponent(tenantId)}/health-history?hours=24`,
});

export const instanceUsageDef = (tenantId: string): AdminQueryDef => ({
  resource: instanceUsageResource(tenantId),
  url: `/api/admin/claws/${encodeURIComponent(tenantId)}/usage-history?hours=24`,
});

// Mirrors the Usage page's "Last 30 days" preset (its default) so the
// prefetched window matches what the page fetches on first render.
export const lastThirtyDayWindow = (now: Date): { start: Date; end: Date } => {
  const end = new Date(now);
  end.setHours(0, 0, 0, 0);
  end.setDate(end.getDate() + 1);
  const start = new Date(end);
  start.setDate(start.getDate() - 30);
  return { start, end };
};

export const adminToolUsageUrl = (start: Date, end: Date): string => {
  const params = new URLSearchParams();
  params.set("start", start.toISOString());
  params.set("end", end.toISOString());
  return `/api/admin/tool-usage?${params.toString()}`;
};

export const adminToolUsageDef = (now: Date): AdminQueryDef => {
  const { start, end } = lastThirtyDayWindow(now);
  return { resource: ADMIN_TOOL_USAGE_RESOURCE, url: adminToolUsageUrl(start, end) };
};
